import { isEqual } from 'lodash';

import { EstateWaterfallComparisonWaterfall } from '@/modules/estateWaterfall/components/EstateWaterfallComparison.type';
import {
  AugmentedCreateComparedWaterfallInput,
  AugmentedUpdateComparedWaterfallInput,
  UpdateComparedWaterfallInput,
  UpdateWaterfallComparisonBundleConfigurationInput,
} from '@/types/schema';

import {
  BundleConfigurationPayload,
  BundleSaveDescriptor,
  DiffBundleConfigurationArgs,
  PresentationSaveContext,
} from '../presentationSave.types';
import { canWriteOwnedConfig } from './flatConfigDescriptor';

/**
 * Whether the rows still line up with the ones the server holds, position for
 * position.
 *
 * `ComparedWaterfall` has no `sortOrder` column: the only thing that conveys
 * order is the order the rows were created in. So an edit that adds, removes or
 * moves a row cannot be expressed as an update, and the list has to be written
 * again from scratch. When the rows line up, each one can be updated in place.
 */
function rowsLineUpWithServer(
  previous: EstateWaterfallComparisonWaterfall[],
  next: EstateWaterfallComparisonWaterfall[]
): boolean {
  if (previous.length !== next.length) return false;
  return previous.every((row, index) => row.id && next[index]?.id === row.id);
}

function toCreate(
  row: EstateWaterfallComparisonWaterfall,
  context: PresentationSaveContext
): AugmentedCreateComparedWaterfallInput {
  return {
    create: {
      householdID: context.householdId,
      estateWaterfallID: row.waterfallId,
      displayName: row.displayName,
      firstDeathYear: row.firstDeathYear,
      secondDeathYear: row.secondDeathYear,
      growthProfileID: row.growthProfileId,
    },
  };
}

/** The fields of one row that changed, or null when none did. */
function diffRow(
  was: EstateWaterfallComparisonWaterfall,
  is: EstateWaterfallComparisonWaterfall
): UpdateComparedWaterfallInput | null {
  const update: UpdateComparedWaterfallInput = {};

  if (was.displayName !== is.displayName) {
    update.displayName = is.displayName;
  }
  // The form uses the empty string for "none chosen", so an unset foreign key
  // goes through the clear flag rather than being sent as an empty id.
  if (was.waterfallId !== is.waterfallId) {
    if (is.waterfallId) {
      update.estateWaterfallID = is.waterfallId;
    } else {
      update.clearEstateWaterfall = true;
    }
  }
  if (was.growthProfileId !== is.growthProfileId) {
    if (is.growthProfileId) {
      update.growthProfileID = is.growthProfileId;
    } else {
      update.clearGrowthProfile = true;
    }
  }
  if (was.firstDeathYear !== is.firstDeathYear) {
    update.firstDeathYear = is.firstDeathYear;
  }
  // Nullable, and zero is a real year, so this checks for null.
  if (was.secondDeathYear !== is.secondDeathYear) {
    if (is.secondDeathYear == null) {
      update.clearSecondDeathYear = true;
    } else {
      update.secondDeathYear = is.secondDeathYear;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/**
 * The waterfall comparison bundle: two page toggles and the list of waterfalls
 * being compared.
 *
 * The title lives on the bundle rather than in the configuration for this kind —
 * the form lifts it out when it loads, and the create path sends it back down —
 * so it is diffed from the bundle here. There is no `clearDisplayName` on this
 * input, so an emptied title is sent as the empty string.
 */
export const waterfallComparisonBundleDescriptor: BundleSaveDescriptor = {
  ownedFields: ['waterfallComparisonBundleConfiguration'],

  canWrite: (previous, next) => {
    const before = previous.waterfallComparisonBundleConfiguration;
    const after = next.waterfallComparisonBundleConfiguration;

    if (after && !after.id && !before?.id) {
      return true;
    }

    return canWriteOwnedConfig(
      (bundle) => bundle.waterfallComparisonBundleConfiguration,
      previous,
      next
    );
  },

  diffConfiguration: ({
    previous,
    next,
    context,
  }: DiffBundleConfigurationArgs): BundleConfigurationPayload | null => {
    const before = previous.waterfallComparisonBundleConfiguration;
    const after = next.waterfallComparisonBundleConfiguration;
    // `canWrite` refused every other combination.
    if (!after) return null;

    if (!after.id) {
      if (isEqual(before, after) && previous.displayName === next.displayName) {
        return null;
      }

      return {
        withWaterfallComparisonBundleConfiguration: {
          create: {
            householdID: context.householdId,
            displayName: next.displayName,
            includeCoverPage: after.includeCoverPage,
            includeAssumptionsPage: after.includeAssumptionsPage,
          },
          withComparedWaterfalls: after.waterfalls.map((row) =>
            toCreate(row, context)
          ),
        },
      };
    }

    if (!before) return null;

    const update: UpdateWaterfallComparisonBundleConfigurationInput = {};
    if (previous.displayName !== next.displayName) {
      update.displayName = next.displayName;
    }
    if (before.includeCoverPage !== after.includeCoverPage) {
      update.includeCoverPage = after.includeCoverPage;
    }
    if (before.includeAssumptionsPage !== after.includeAssumptionsPage) {
      update.includeAssumptionsPage = after.includeAssumptionsPage;
    }

    if (!rowsLineUpWithServer(before.waterfalls, after.waterfalls)) {
      // Written again from scratch, because creation order is the only thing
      // that carries position. Nothing outside the configuration addresses these
      // rows — the slides read the list, and the context map is keyed by bundle —
      // so replacing them costs nothing but their ids.
      update.clearComparedWaterfalls = true;

      return {
        updateWaterfallComparisonBundleConfiguration: {
          id: after.id,
          update,
          withComparedWaterfalls: after.waterfalls.map((row) =>
            toCreate(row, context)
          ),
        },
      };
    }

    const rows = after.waterfalls.reduce<
      AugmentedUpdateComparedWaterfallInput[]
    >((entries, row, index) => {
      // `rowsLineUpWithServer` established both of these.
      const was = before.waterfalls[index];
      if (!was || !row.id) return entries;

      const rowUpdate = diffRow(was, row);
      if (rowUpdate) entries.push({ id: row.id, update: rowUpdate });
      return entries;
    }, []);

    if (Object.keys(update).length === 0 && rows.length === 0) {
      return null;
    }

    return {
      updateWaterfallComparisonBundleConfiguration: {
        id: after.id,
        update,
        ...(rows.length > 0 ? { updateComparedWaterfalls: rows } : {}),
      },
    };
  },
};
